export interface Test {
  id: string;
  question: string;
  options: string[];
}

export interface Lesson {
  id: string;
  title: string;
  content: string | null;
  videoUrl: string | null;
  /**
   * Video Bunny'da, Bunny esa hozir ishlamayapti — `videoUrl` null.
   * Qora pleyer o'rniga aniq xabar ko'rsatiladi.
   */
  videoUnavailable?: boolean;
  materialUrl: string | null;
  order: number;
  isUnlocked: boolean;
  isCompleted: boolean;
  score: number | null;
  tests: Test[];
  notes?: string;
  isVideoRequired?: boolean;
  videoWatchPercent?: number;
  videoWatchedPercent?: number;
  isVideoCompleted?: boolean;
  /** Videoni qaysi soniyadan davom ettirish kerak. */
  videoPositionSec?: number;
  /** Ustoz alohida bergan qo'shimcha urinishlar. */
  extraAttempts?: number;
  /** Tanga evaziga navbatsiz ochilganmi. */
  unlockedByCoins?: boolean;
  testMaxAttempts?: number;
  testTimeLimitSec?: number;
  testAttempts?: number;
  testStartedAt?: string | null;
  analysisVideoUrl?: string | null;
  analysisVideoUnavailable?: boolean;

  testType?: 'QUIZ' | 'FILE_TEST';
  testFileUrl?: string | null;
  testCheckMethod?: 'AUTO' | 'AI' | 'MANUAL';

  fileTestStatus?: 'PENDING' | 'CHECKING' | 'GRADED';
  fileTestAiFeedback?: string | null;
  fileTestStudentAnswer?: string | null;
  fileTestStudentFile?: string | null;
  fileTestAttempts?: number;

  // Muddat (deadline) ma'lumotlari
  unlockedAt?: string | null;
  deadlineAt?: string | null;
  deadlineMissed?: boolean;
  deadlineDays?: number;
}

export interface Module {
  id: string;
  title: string;
  order: number;
  lessons: Lesson[];
}

export interface CoinSettings {
  enabled: boolean;
  /** Narx 0 => o'sha imkoniyat o'chirilgan. */
  extraAttempt: number;
  reopenLesson: number;
  skipLesson: number;
}

export interface CourseProgress {
  id: string;
  title: string;
  description: string | null;
  /**
   * Server aslida `{ course: {...}, modules: [...] }` qaytaradi. Yuqoridagi
   * yassi maydonlar eski kod uchun qolgan; yangi maydonlar shu yerda.
   */
  course?: {
    id: string;
    title: string;
    description: string | null;
    /** 100% natija uchun beriladigan XP. Tanga — uning 1/5 qismi. */
    xpPer100Percent?: number;
    coins?: CoinSettings;
  };
  modules: Module[];
}

export interface Certificate {
  id: string;
  issuedAt: string;
  course: {
    id: string;
    title: string;
    description: string | null;
  };
}

export interface TeacherGroup {
  id: string;
  name: string;
  inviteCode?: string;
  courseId?: string;
  course?: { id: string; title: string };
  _count?: { enrollments: number };
}

export interface LeaderboardItem {
  rank: number;
  medal: string;
  studentId: string;
  fullName: string;
  completedLessons: number;
  totalLessons: number;
  percent: number;
  // Backend bularni qaytaradi va LeaderboardPage ularni ishlatadi, lekin
  // tipda yo'q edi (webapp build'i tsc ni ishlatmagani uchun sezilmagan).
  xp: number;
  streak: number;
  coins: number;
}

import axios from 'axios';
import { currentLanguage } from '../i18n';

// In production: backend serves both static files AND API (no /api prefix needed)
// In dev: Vite proxy strips /api before forwarding to backend
const isProduction = import.meta.env.PROD;
const BASE_URL = isProduction ? '' : '/api';

const axiosClient = axios.create({ baseURL: BASE_URL });

/**
 * Backend xatosidan foydalanuvchiga ko'rsatiladigan matnni ajratib olish.
 *
 * `AllExceptionsFilter` konverti — `{ success: false, data: null, error }`.
 * Kalit `error`, `message` EMAS.
 *
 * Bu shunchaki did masalasi emas: chaqiruvchilarning bir qismi
 * `data.message` ni o'qirdi, u esa hech qachon mavjud emas. Natijada
 * backendning aniq sababi ("Urinishlar soni tugagan", "Bu darsning
 * muddati o'tgan") o'quvchiga YETIB BORMASDI — u har doim umumiy
 * "Saqlab bo'lmadi" ni ko'rardi.
 *
 * `message` zaxira sifatida qoldirildi: NestJS'ning o'z validatsiya
 * javoblari filtrdan o'tmagan holatda shu shaklda kelishi mumkin.
 */
export function apiErrorMessage(err: any, fallback: string): string {
  const d = err?.response?.data;
  const m = d?.error ?? d?.message;
  if (Array.isArray(m)) return m[0] ?? fallback; // class-validator massiv qaytaradi
  return (typeof m === 'string' && m) || fallback;
}

/**
 * Dars media manzilini brauzer ocha oladigan URL ga aylantiradi.
 *
 * `telegram:<file_id>` havolalarini BACKEND imzolaydi va tayyor
 * `/api/video/<id>?exp=…&sig=…` ko'rinishida qaytaradi (qarang
 * `common/utils/media-url.util.ts`) — chunki `<video src>` maxsus sarlavha
 * yubora olmaydi, kirish nazorati esa kerak.
 *
 * Demak bu yerga hali ham xom `telegram:` kelsa, bu backend uni imzolamagani
 * degani. Bunda `null` qaytaramiz: imzosiz havola baribir 403 olardi, "video
 * yo'q" deb ko'rsatgan tushunarliroq.
 */
export function resolveMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('telegram:')) {
    console.warn('Imzolanmagan telegram media havolasi keldi:', url);
    return null;
  }
  /*
   * `bunny:<guid>` ham xuddi shunday: uni BACKEND imzolangan iframe
   * manziliga aylantiradi (`mediaManzil`). Bu yerga xom holda kelsa,
   * demak biror yo'l uni aylantirmagan.
   *
   * Ilgari u shunchaki qaytarilardi va natija jimgina buzilardi:
   * `isBunnyVideo` ham, `isNativeVideo` ham yolg'on chiqib, havola
   * `ReactPlayer` ga tushardi — u esa hech narsa chizmaydi, ya'ni
   * o'quvchi sababi ko'rinmaydigan QORA QUTI ko'rardi.
   */
  if (url.startsWith('bunny:')) {
    console.warn('Imzolanmagan bunny media havolasi keldi:', url);
    return null;
  }
  return url;
}

axiosClient.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject(error),
);

axiosClient.interceptors.request.use(async (config) => {
  const { useAppStore } = await import('../store/useAppStore');
  const state = useAppStore.getState();
  if (state.tgInitData) {
    config.headers['x-tg-init-data'] = state.tgInitData;
  } else if (state.webToken && !config.headers.Authorization) {
    /*
     * SAYT SESSIYASI — Telegramdan tashqarida.
     *
     * `initData` ustun turadi: Telegram ichida u har doim yangi, veb
     * token esa eskirgan boʻlishi mumkin.
     *
     * Mavjud `Authorization` ustidan YOZILMAYDI — ustoz chaqiruvlari
     * oʻz tokenini oʻzi qoʻyadi (`teacherAuth`).
     */
    config.headers.Authorization = `Bearer ${state.webToken}`;
  }
  /*
   * Tanlangan kurs — server qaysi a'zolikni ishlatishni shundan biladi.
   *
   * So'rovlarning ko'pchiligida `courseId` yoki `lessonId` allaqachon bor
   * va guard uni o'zi topadi, lekin bir qismida (profil, tanga, reyting)
   * yo'q. Ular uchun bu sarlavha bo'lmasa server BIRINCHI a'zolikni
   * oladi — ya'ni ikkinchi kursdagi o'quvchi doim birinchi kursning
   * ma'lumotini ko'rardi.
   */
  if (state.courseId) {
    config.headers['x-course-id'] = state.courseId;
  }
  // Server xato xabarlarini shu tilda qaytaradi (`translate-message.ts`).
  config.headers['x-lang'] = currentLanguage();
  return config;
});

// Override global fetch locally for this module to utilize axios
async function customFetch(endpoint: string, options: any = {}) {
  try {
    const { useAppStore } = await import('../store/useAppStore');
    const state = useAppStore.getState();
    const headers = { ...options.headers };

    if (state.tgInitData) {
      headers['x-tg-init-data'] = state.tgInitData;
    } else if (state.webToken && !headers.Authorization) {
      // Sayt sessiyasi. `fetchCourseProgress` shu yoʻldan oʻtadi, yaʼni
      // busiz saytdagi oʻquvchi darslarni umuman koʻra olmasdi.
      headers.Authorization = `Bearer ${state.webToken}`;
    }

    // FormData ni JSON.parse qilib bo'lmaydi — ilgari shu sabab har qanday
    // fayl yuklash 500 bilan yiqilardi.
    const body = options.body;
    let data: unknown;
    if (body === undefined || body === null) {
      data = undefined;
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      data = body;
      delete headers['Content-Type']; // boundary ni axios o'zi qo'ysin
    } else if (typeof body === 'string') {
      data = JSON.parse(body);
    } else {
      data = body;
    }

    const res = await axiosClient.request({
      url: endpoint.replace(BASE_URL, ''),
      method: options.method || 'GET',
      headers,
      data,
    });

    return {
      ok: true,
      json: async () =>
        res.data?.data !== undefined ? res.data.data : res.data,
    };
  } catch (err: any) {
    const errMsg =
      err.response?.data?.error || err.response?.data?.message || err.message;
    return {
      ok: false,
      status: err.response?.status || 500,
      json: async () => ({ message: errMsg }),
    };
  }
}

// Map old fetch to customFetch
const fetch = customFetch;

export async function fetchCourseProgress(
  studentId: string,
  courseId: string,
): Promise<CourseProgress> {
  const res = await fetch(
    `${BASE_URL}/students/me/courses/${courseId}/progress`,
    {
      headers: {
        'x-student-id': studentId,
        'Content-Type': 'application/json',
      },
    },
  );
  if (!res.ok) {
    throw new Error('Kurs progress maʼlumotlarini yuklashda xatolik');
  }
  return res.json();
}

export async function submitLessonTest(
  studentId: string,
  lessonId: string,
  answers: { testId: string; selectedOption: number }[],
): Promise<{
  message: string;
  score: number;
  isCompleted: boolean;
  resultsMap?: Record<string, boolean>;
  correctOptionsMap?: Record<string, number>;
}> {
  const res = await fetch(
    `${BASE_URL}/students/lessons/${lessonId}/submit-test`,
    {
      method: 'POST',
      headers: {
        'x-student-id': studentId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ answers }),
    },
  );
  if (!res.ok) {
    throw new Error('Test natijalarini yuborishda xato bering');
  }
  return res.json();
}

export async function fetchMyCertificates(
  studentId: string,
): Promise<Certificate[]> {
  const res = await fetch(`${BASE_URL}/students/me/certificates`, {
    headers: {
      'x-student-id': studentId,
    },
  });
  if (!res.ok) return [];
  return res.json();
}

export async function fetchTeacherGroups(
  token: string,
): Promise<TeacherGroup[]> {
  const res = await fetch(`${BASE_URL}/groups`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Guruhlarni yuklashda xatolik');
  return res.json();
}

export async function createGroupApi(
  token: string,
  courseId: string,
  name: string,
) {
  const res = await fetch(`${BASE_URL}/groups`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ courseId, name }),
  });
  if (!res.ok) throw new Error('Guruh yaratishda xatolik');
  return res.json();
}

export async function getGroupStudentsApi(token: string, groupId: string) {
  const res = await fetch(`${BASE_URL}/groups/${groupId}/students`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("O'quvchilarni yuklashda xatolik");
  return res.json();
}

export async function addStudentApi(
  token: string,
  groupId: string,
  data: {
    fullName: string;
    phone: string;
    telegramId?: number;
    status?: string;
  },
) {
  const res = await axiosClient.post(
    `/groups/${groupId}/students`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function updateStudentApi(
  token: string,
  studentId: string,
  data: { fullName?: string; phone?: string; status?: string },
) {
  const res = await axiosClient.patch(
    `/students/${studentId}`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function deleteStudentApi(token: string, studentId: string) {
  const res = await axiosClient.delete(
    `/students/${studentId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/** O'quvchi ko'rinishidagi reyting — initData bilan, tokensiz. */
export async function fetchLeaderboard(
  groupId: string,
): Promise<LeaderboardItem[]> {
  const res = await fetch(`${BASE_URL}/students/groups/${groupId}/leaderboard`);
  if (!res.ok) return [];
  return res.json();
}

export async function getGroupInviteLinkApi(groupId: string) {
  const res = await fetch(`${BASE_URL}/groups/${groupId}/invite-link`);
  if (!res.ok) throw new Error('Taklif havolasini olishda xatolik');
  return res.json();
}

export async function updateGroupApi(
  token: string,
  groupId: string,
  data: { name?: string; courseId?: string },
) {
  const res = await fetch(`${BASE_URL}/groups/${groupId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Guruhni o'zgartirishda xatolik");
  return res.json();
}

export async function deleteGroupApi(token: string, groupId: string) {
  const res = await fetch(`${BASE_URL}/groups/${groupId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Guruhni o'chirishda xatolik");
  return res.json();
}

export async function fetchStudentByTelegramId(telegramId: number) {
  const res = await customFetch(`/students/progress/${telegramId}`);
  if (!res.ok) throw new Error('Oʻquvchi topilmoqda xato');
  return res.json();
}

export async function sendCertificateToTelegramApi(
  studentId: string,
  data: {
    imageData: string;
    courseTitle: string;
    certId: string;
    date: string;
  },
) {
  const res = await fetch(`${BASE_URL}/students/me/send-certificate`, {
    method: 'POST',
    headers: {
      'x-student-id': studentId,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Sertifikatni yuborib boʻlmadi');
  return res.json();
}

export async function sendChatMessageApi(studentId: string, message: string) {
  const res = await fetch(`${BASE_URL}/students/me/chat`, {
    method: 'POST',
    headers: {
      'x-student-id': studentId,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });
  if (!res.ok) throw new Error('Xabar yuborishda xatolik');
  return res.json();
}

export async function explainMistakesApi(
  studentId: string,
  lessonId: string,
  mistakes: { testId: string; selectedOptionIndex: number }[],
) {
  const res = await fetch(`${BASE_URL}/students/me/knowledge-gap`, {
    method: 'POST',
    headers: {
      'x-student-id': studentId,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ lessonId, mistakes }),
  });
  if (!res.ok) throw new Error('Xatolarni tahlil qilishda xatolik');
  return res.json();
}

// =======================
// TEACHER API METHODS
// =======================

export async function teacherTelegramLogin(
  telegramId: number,
): Promise<{ access_token: string; user: any }> {
  const res = await fetch(`${BASE_URL}/auth/telegram-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ telegramId }),
  });
  if (!res.ok) throw new Error('Oʻqituvchi sifatida tizimga kirib boʻlmadi');
  return res.json();
}

export async function createCourseApi(
  token: string,
  data: {
    title: string;
    description?: string;
    lessonPassThreshold?: number;
    modulePassThreshold?: number;
    xpPer100Percent?: number;
    accessDurationDays?: number;
    inactivityWarningDays?: number;
    inactivityBlockDays?: number;
  },
) {
  const res = await fetch(`${BASE_URL}/courses`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Kurs yaratishda xatolik');
  return res.json();
}

export interface UploadedFileInfo {
  url: string;
  filename: string;
  originalName: string;
  size: number;
  mimetype: string;
}

function unwrap<T>(payload: any): T {
  return (payload?.data !== undefined ? payload.data : payload) as T;
}

/** O'qituvchi uchun (JWT bilan) — dars materiallari, videolar. */
export async function uploadFileApi(
  token: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadedFileInfo> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axiosClient.post('/upload', formData, {
    headers: { Authorization: `Bearer ${token}` },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
  });

  return unwrap<UploadedFileInfo>(res.data);
}

/**
 * O'quvchi uchun (Telegram initData bilan) — topshiriq fayli.
 * Alohida endpoint, chunki o'quvchida JWT yo'q.
 */
/**
 * O'quvchining topshiriq faylini yuklaydi.
 *
 * `onProgress` `null` qaytarsa — hajm nomaʼlum, ya'ni foizni hisoblab
 * bo'lmaydi (ba'zi WebView'lar `e.total` ni bermaydi). Chaqiruvchi bunday
 * holatda "0%" emas, "Yuklanmoqda…" ko'rsatishi kerak.
 */
export async function uploadStudentFileApi(
  file: File,
  onProgress?: (percent: number | null) => void,
): Promise<UploadedFileInfo> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await axiosClient.post('/students/upload', formData, {
    /*
     * MUDDAT CHEGARASI SHART.
     *
     * Axios'da standart timeout YO'Q — so'rov javob kelmasa CHEKSIZ
     * kutadi. Telegram WebView'da aynan shu bo'ldi: yuklash boshlandi,
     * so'rov serverga umuman yetib bormadi (nginx logida ham, ilova
     * logida ham izi yo'q), promise esa hech qachon hal bo'lmadi —
     * o'quvchi "0%" da muzlab qolgan ekranni ko'rdi, na xato, na natija.
     * Endi 90 soniyadan keyin tushunarli xato chiqadi.
     */
    timeout: 90_000,
    timeoutErrorMessage:
      'Fayl yuklanmadi — aloqa uzildi. Internetni tekshirib qayta urining.',
    onUploadProgress: (e) => {
      if (!onProgress) return;
      // `e.total` yo'q bo'lsa foiz ma'nosiz — `null` yuboramiz.
      onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : null);
    },
  });

  return unwrap<UploadedFileInfo>(res.data);
}

export async function createModuleApi(
  token: string,
  courseId: string,
  data: { title: string; order: number },
) {
  const res = await fetch(`${BASE_URL}/courses/${courseId}/modules`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Modul yaratishda xatolik');
  return res.json();
}

export async function createLessonApi(
  token: string,
  moduleId: string,
  data: {
    title: string;
    order: number;
    videoUrl?: string;
    content?: string;
    materialUrl?: string;
    isVideoRequired?: boolean;
    videoWatchPercent?: number;
    testMaxAttempts?: number;
    testTimeLimitSec?: number;
    analysisVideoUrl?: string;
    deadlineDays?: number;
  },
) {
  const res = await fetch(`${BASE_URL}/modules/${moduleId}/lessons`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Dars yaratishda xatolik');
  return res.json();
}

export async function generateAiTestsApi(
  token: string,
  content: string,
  count: number,
) {
  const res = await fetch(`${BASE_URL}/ai/generate-tests`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content, count }),
  });
  if (!res.ok) throw new Error('AI test yaratishda xatolik');
  return res.json();
}

export async function saveLessonNotes(
  // studentId backend tomonidan initData'dan olinadi; imzo saqlanmoqda,
  // chunki chaqiruv joylari uni uzatadi.
  _studentId: string,
  lessonId: string,
  notes: string,
) {
  const { data } = await axiosClient.post(
    `/students/lessons/${lessonId}/notes`,
    { notes },
  );
  return data;
}

export interface TeacherStudentLesson {
  id: string;
  progressId: string | null;
  title: string;
  order: number;
  isCompleted: boolean;
  score: number | null;
  attempts: number;
  maxAttempts: number;
  extraAttempts: number;
  deadlineMissed: boolean;
}

export interface TeacherStudentDetail {
  student: { id: string; fullName: string; phone: string };
  group: { id: string; name: string };
  course: { id: string; title: string } | null;
  summary: {
    completionPercent: number;
    completedLessons: number;
    totalLessons: number;
    averageScore: number | null;
    missedDeadlines: number;
  };
  modules: { id: string; title: string; lessons: TeacherStudentLesson[] }[];
}

/** Ustoz: bitta o'quvchining kurs bo'yicha to'liq manzarasi. */
export async function fetchTeacherStudentDetail(
  token: string,
  studentId: string,
): Promise<TeacherStudentDetail> {
  const res = await axiosClient.get(`/teacher/progress/students/${studentId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data?.data ?? res.data;
}

/** Ustoz: o'quvchiga qo'shimcha urinish berish. */
export async function grantExtraAttemptsApi(
  token: string,
  progressId: string,
  count = 1,
) {
  const res = await axiosClient.post(
    `/teacher/progress/${progressId}/grant-attempts`,
    { count },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data?.data ?? res.data;
}

/**
 * Ustoz uchun JWT — Telegram initData evaziga.
 *
 * `/teacher/*` marshrutlari `JwtAuthGuard` bilan himoyalangan, ya'ni
 * initData ularga yaramaydi. Shu sababli ustoz rejimiga kirganda bir
 * marta token olinadi va do'konda saqlanadi.
 *
 * Bu chaqiruv ILGARI HECH QAYERDA yo'q edi: `setTeacherToken` do'konda
 * e'lon qilingan-u, hech kim uni chaqirmagan. Natijada `teacherToken`
 * doim `null` bo'lib, Mini App'dagi ustoz rejimi hech qachon
 * ishlamagan — guruhlar ro'yxati bo'sh, so'rovlar 401.
 */
export async function teacherLoginApi(): Promise<{
  access_token: string;
  user: { id: string; name: string; phone: string; role: string };
}> {
  const res = await axiosClient.post('/auth/telegram-login', {});
  return res.data?.data ?? res.data;
}

/** Bitta kursdagi a'zolik — o'quvchi bir nechta kursda bo'lishi mumkin. */
export interface Enrollment {
  enrollmentId: string;
  groupId: string;
  groupName: string | null;
  courseId: string | null;
  courseTitle: string | null;
  status: 'PENDING' | 'APPROVED' | 'BLOCKED';
  /** Ball har kursda ALOHIDA — reyting guruh ichida tuziladi. */
  xp: number;
  /**
   * Guruh majburiy kanal talab qilsa. `member: false` — kurs soʻrovlari
   * 403 qaytaradi, ilova kanal ekranini (`ChannelGate`) koʻrsatadi.
   */
  channel?: { link: string | null; member: boolean } | null;
}

export type Identity =
  | { role: 'teacher'; teacherId: string; name: string | null }
  | {
      role: 'student';
      studentId: string;
      /**
       * Saytda BU YAGONA MANBA — Telegram SDK yoʻq.
       * Telegram ichida SDK'niki ustun turadi.
       */
      telegramId: string | null;
      // Faol a'zolik — server so'rovdan aniqlagan.
      groupId: string | null;
      courseId: string | null;
      courseTitle: string | null;
      status: 'PENDING' | 'APPROVED' | 'BLOCKED' | null;
      name: string | null;
      /** Barcha kurslar. Bo'sh bo'lishi mumkin (eski yozuvlar). */
      enrollments: Enrollment[];
    };

/**
 * "Men kimman" — initData bo'yicha.
 *
 * Ilgari rol, `studentId` va `courseId` FAQAT URL parametrlaridan
 * olinardi, ya'ni ular menyu tugmasi manziliga yozib qo'yilgan eski
 * qiymatlar bo'lishi mumkin edi. Endi haqiqat manbai — server.
 */
/** @param fresh — kanal aʼzoligini keshsiz tekshirish ("Tekshirish" tugmasi). */
export async function fetchIdentity(fresh = false): Promise<Identity> {
  const res = await axiosClient.get('/auth/me', {
    params: fresh ? { fresh: 1 } : undefined,
  });
  return unwrap<Identity>(res.data);
}

/** Onlayn puls + sanoq. 30 soniyada bir marta chaqiriladi. */
export async function presenceApi(courseId: string, lessonId?: string) {
  const res = await axiosClient.post('/students/me/presence', {
    courseId,
    ...(lessonId && { lessonId }),
  });
  return (res.data?.data ?? res.data) as { course: number; lesson: number };
}

export type CoinAction = 'EXTRA_ATTEMPT' | 'REOPEN_LESSON' | 'SKIP_LESSON';

/** Tanga sarflash. Server balansni ATOMIK ayiradi — mijozda tekshirmaymiz. */
export async function spendCoinsApi(lessonId: string, action: CoinAction) {
  const res = await axiosClient.post(
    `/students/lessons/${lessonId}/spend-coins`,
    { action },
  );
  return (res.data?.data ?? res.data) as {
    ok: boolean;
    spent: number;
    coins: number;
    action: CoinAction;
  };
}

export async function saveVideoProgressApi(
  lessonId: string,
  percent: number,
  isCompleted: boolean,
  positionSec?: number,
) {
  const res = await axiosClient.post(
    `/students/lessons/${lessonId}/video-progress`,
    { percent, isCompleted, ...(positionSec !== undefined && { positionSec }) },
  );
  return res.data;
}

export async function submitFileTest(
  lessonId: string,
  data: { answerText?: string; fileUrl?: string },
) {
  const res = await axiosClient.post(
    `/students/lessons/${lessonId}/submit-file-test`,
    data,
  );
  return res.data;
}

export async function resetFileTest(lessonId: string) {
  const res = await axiosClient.post(
    `/students/lessons/${lessonId}/reset-file-test`,
  );
  return res.data;
}

export async function startTestApi(lessonId: string) {
  const res = await axiosClient.post(
    `/students/lessons/${lessonId}/start-test`,
  );
  return res.data;
}

export async function generateModuleReviewApi(
  moduleId: string,
): Promise<{ review: string }> {
  const res = await axiosClient.post('/ai/module-review', { moduleId });
  return res.data?.data !== undefined ? res.data.data : res.data;
}

/* ==========================================================================
   USTOZ API — Mini App ichidagi ustoz rejimi uchun.

   Bu yerdagi funksiyalar `axiosClient` ustida (customFetch emas) va JWT ni
   parametr sifatida oladi, chunki ustoz tokeni Telegram initData'dan emas,
   `useAppStore.teacherToken` dan keladi.
   ========================================================================== */

function teacherAuth(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

/** Guruhning to'liq manzarasi: o'quvchilar + progress + to'lov + holat. */
export interface TeacherGroupStudent {
  id: string;
  fullName: string;
  phone: string;
  age: number;
  status: 'PENDING' | 'APPROVED' | 'BLOCKED';
  telegramId: string;
  joinedAt: string;
  lastActiveAt: string | null;
  courseAccessUntil: string | null;
  accessExpired: boolean;
  xp: number;
  streak: number;
  completedLessons: number;
  totalLessons: number;
  completionPercent: number;
  payment: {
    id: string;
    status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
    amount: string;
    dueDate: string | null;
    paidAt: string | null;
  } | null;
}

export interface TeacherGroupOverview {
  group: {
    id: string;
    name: string;
    inviteCode: string;
    course: { id: string; title: string | null };
    totalLessons: number;
  };
  summary: {
    totalStudents: number;
    approved: number;
    pending: number;
    blocked: number;
    averageCompletion: number;
    paymentsOverdue: number;
    paymentsPending: number;
    accessExpired: number;
  };
  students: TeacherGroupStudent[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export async function fetchGroupOverview(
  token: string,
  groupId: string,
  params?: { page?: number; limit?: number },
): Promise<TeacherGroupOverview> {
  const res = await axiosClient.get(`/groups/${groupId}/overview`, {
    ...teacherAuth(token),
    params,
  });
  return res.data?.data ?? res.data;
}

export async function fetchTeacherCourses(token: string) {
  const res = await axiosClient.get('/courses', teacherAuth(token));
  return res.data?.data ?? res.data;
}

export async function updateCourseApi(
  token: string,
  courseId: string,
  data: Record<string, unknown>,
) {
  const res = await axiosClient.patch(
    `/courses/${courseId}`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function deleteCourseApi(token: string, courseId: string) {
  const res = await axiosClient.delete(
    `/courses/${courseId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function updateModuleApi(
  token: string,
  courseId: string,
  moduleId: string,
  data: { title?: string; order?: number },
) {
  const res = await axiosClient.patch(
    `/courses/${courseId}/modules/${moduleId}`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function deleteModuleApi(
  token: string,
  courseId: string,
  moduleId: string,
) {
  const res = await axiosClient.delete(
    `/courses/${courseId}/modules/${moduleId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function updateLessonApi(
  token: string,
  lessonId: string,
  data: Record<string, unknown>,
) {
  const res = await axiosClient.patch(
    `/modules/lessons/${lessonId}`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function deleteLessonApi(token: string, lessonId: string) {
  const res = await axiosClient.delete(
    `/modules/lessons/${lessonId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function fetchLessonTests(token: string, lessonId: string) {
  const res = await axiosClient.get(
    `/lessons/${lessonId}/tests-admin`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function createTestApi(
  token: string,
  lessonId: string,
  data: {
    question: string;
    options: string[];
    correctOption: number;
    explanation?: string;
  },
) {
  const res = await axiosClient.post(
    `/lessons/${lessonId}/tests`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function updateTestApi(
  token: string,
  testId: string,
  data: Record<string, unknown>,
) {
  const res = await axiosClient.patch(
    `/lessons/tests/${testId}`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function deleteTestApi(token: string, testId: string) {
  const res = await axiosClient.delete(
    `/lessons/tests/${testId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/*
 * ——— Saytdan kirish (oʻquvchi) ———
 *
 * Telegramdan tashqarida `initData` yoʻq, shuning uchun sessiya
 * tokenini olamiz. Ikkala chaqiruv ham tokensiz — bular kirish yoʻli.
 */

export interface WebSession {
  access_token: string;
  user: { id: string; name: string; role: string };
}

export async function webLoginWidgetApi(
  fields: Record<string, string>,
): Promise<WebSession> {
  const res = await axiosClient.post('/auth/web/telegram', fields);
  return res.data?.data ?? res.data;
}

export async function webLoginLinkApi(token: string): Promise<WebSession> {
  const res = await axiosClient.post('/auth/web/link', { token });
  return res.data?.data ?? res.data;
}

/*
 * ——— Toʻlovlar (ustoz) ———
 *
 * Mini App'da toʻlov OʻQUVCHI ekranida turadi, admin paneldagidek
 * alohida sahifada emas: telefonda ustoz odatda bitta odam haqida
 * savol beradi ("toʻladimi?"), butun roʻyxatni varaqlamaydi.
 */

export interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate?: string | null;
  paidAt?: string | null;
  description?: string | null;
  createdAt?: string;
}

export async function getStudentPaymentsApi(
  token: string,
  studentId: string,
): Promise<Payment[]> {
  const res = await axiosClient.get(
    `/payments/student/${studentId}`,
    teacherAuth(token),
  );
  const d = res.data?.data ?? res.data;
  // Server sahifalangan yoki oddiy roʻyxat qaytarishi mumkin.
  return Array.isArray(d) ? d : (d?.items ?? []);
}

export async function createPaymentApi(
  token: string,
  data: {
    studentId: string;
    amount: number;
    status?: string;
    dueDate?: string;
    paidAt?: string;
    description?: string;
  },
) {
  const res = await axiosClient.post('/payments', data, teacherAuth(token));
  return res.data?.data ?? res.data;
}

export async function deletePaymentApi(token: string, paymentId: string) {
  const res = await axiosClient.delete(
    `/payments/${paymentId}`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/*
 * ——— Eʼlon va reyting (ustoz) ———
 *
 * Ikkalasi ham GURUHGA tegishli, shuning uchun Mini App'da guruh
 * ekranining ichida turadi — telefonda alohida bo'lim ochib, yana
 * guruh tanlashdan ko'ra qisqaroq yo'l.
 *
 * Reyting uchun o'quvchiniki EMAS, ustoznikidan foydalaniladi:
 * `/students/groups/:id/leaderboard` initData bilan ishlaydi va
 * o'quvchi ko'rinishini beradi.
 */

export async function sendBroadcastApi(
  token: string,
  groupId: string,
  message: string,
) {
  const res = await axiosClient.post(
    `/broadcast/group/${groupId}`,
    { message },
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function getBroadcastHistoryApi(token: string, groupId: string) {
  const res = await axiosClient.get(
    `/broadcast/group/${groupId}/history`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export interface TeacherLeaderboardRow {
  rank: number;
  medal?: string;
  studentId: string;
  fullName: string;
  completedLessons: number;
  totalLessons: number;
  percent: number;
  xp: number;
  streak: number;
  coins: number;
}

export async function getTeacherGroupLeaderboardApi(
  token: string,
  groupId: string,
): Promise<TeacherLeaderboardRow[]> {
  const res = await axiosClient.get(
    `/groups/${groupId}/leaderboard`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/*
 * ——— Faylli topshiriqlarni baholash ———
 *
 * Bu ish telefonga eng mos: ustoz yo'lda ketayotib bir nechta ishni
 * ko'rib chiqa oladi. Shunga qaramay u faqat admin panelda bor edi.
 *
 * Yo'l nomlari admin paneldagi bilan AYNAN bir xil bo'lishi shart —
 * `npm run marshrut` ikkalasini ham kontrollerlar bilan solishtiradi.
 */

export interface FileSubmission {
  id: string;
  score: number | null;
  fileTestStatus: 'CHECKING' | 'GRADED';
  fileTestStudentAnswer: string | null;
  fileTestStudentFile: string | null;
  fileTestAiFeedback: string | null;
  updatedAt: string;
  student: { id: string; fullName: string; phone: string };
  lesson: {
    id: string;
    title: string;
    testFileUrl: string | null;
    testCheckMethod: string | null;
    testAnswerKey: string | null;
    testAiRubric: string | null;
  };
}

export async function getPendingFileTestsApi(
  token: string,
  groupId?: string,
): Promise<FileSubmission[]> {
  /*
   * Yo'l HARF BILAN yozilgan, ternary bilan yasalmagan.
   *
   * `npm run marshrut` chaqiruvlarni matn bo'yicha topadi — o'zgaruvchi
   * yoki ternary bilan qurilgan yo'l uning ko'zidan qochib ketadi, ya'ni
   * xato manzil jimgina qolib ketishi mumkin. Filtr esa `params` orqali.
   */
  const res = await axiosClient.get('/teacher/progress/file-tests/pending', {
    ...teacherAuth(token),
    params: groupId ? { groupId } : undefined,
  });
  return res.data?.data ?? res.data;
}

export async function gradeFileTestApi(
  token: string,
  progressId: string,
  data: { score: number; feedback: string },
) {
  const res = await axiosClient.post(
    `/teacher/progress/file-tests/${progressId}/grade`,
    data,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/*
 * ——— Video xostingi (Bunny Stream) ———
 *
 * Ustoz rejimi allaqachon haqiqiy JWT bilan ishlaydi (`/upload` shu
 * bilan himoyalangan), shuning uchun panel ishlatadigan marshrutlarning
 * aynan o'zi bu yerda ham ochiq — backendga qo'shimcha kerak emas.
 */

export interface VideoUploadPermission {
  videoId: string;
  libraryId: string;
  expire: number;
  signature: string;
  endpoint: string;
}

export async function getVideoConfigApi(
  token: string,
): Promise<{ enabled: boolean; bunnyPlayable?: boolean }> {
  const res = await axiosClient.get('/video/config', teacherAuth(token));
  return res.data?.data ?? res.data;
}

export async function getVideoUploadSignatureApi(
  token: string,
  title: string,
): Promise<VideoUploadPermission> {
  const res = await axiosClient.post(
    '/video/upload-signature',
    { title },
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

export async function getVideoStatusApi(
  token: string,
  videoId: string,
): Promise<{ status: number; ready: boolean; percent: number }> {
  const res = await axiosClient.get(
    `/video/${videoId}/status`,
    teacherAuth(token),
  );
  return res.data?.data ?? res.data;
}

/** Ustoz fayl yuklashi — o'quvchinikidan alohida, JWT bilan. */
export async function uploadTeacherFileApi(
  token: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await axiosClient.post('/upload', form, {
    headers: { Authorization: `Bearer ${token}` },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
  });
  const data = res.data?.data ?? res.data;
  return { url: data.url };
}

// ——— Sinov imtihonlari (Milliy sertifikat shakli) ———

/** `grading` — yopilgan, lekin yozma ishlar hali tekshirilmoqda. */
export type ExamPhase = 'upcoming' | 'open' | 'closed' | 'grading' | 'scored';

/**
 * `OPEN` — oʻquvchi javobni oʻzi yozadi (avtomat tekshiriladi).
 * `WRITTEN` — yozma ish: `AUTO` da bandlarga (a, b, c…) boʻlingan javob,
 * `MANUAL` da rasm/matn, ustoz baholaydi.
 */
export type ExamItemType = 'SINGLE_CHOICE' | 'OPEN' | 'WRITTEN';
export type WrittenGrading = 'MANUAL' | 'AUTO';
/** `ANSWER_SHEET` — savol matni yoʻq, faqat raqamlar va doirachalar. */
export type ExamMode = 'QUESTIONS' | 'ANSWER_SHEET';
export type ExamResultMode = 'IMMEDIATE' | 'AFTER_CLOSE';
export type ExamOutcome =
  'CORRECT' | 'PARTIAL' | 'WRONG' | 'UNANSWERED' | 'PENDING';

export interface StudentExamPart {
  id: string;
  title: string;
  durationMin: number;
  itemCount: number;
  maxScore: number;
}

export interface StudentExam {
  id: string;
  title: string;
  subject: string | null;
  description: string | null;
  code: string;
  mode: ExamMode;
  resultMode: ExamResultMode;
  startsAt: string;
  endsAt: string;
  /** Barcha qismlar yigʻindisi. */
  durationMin: number;
  itemCount: number;
  parts: StudentExamPart[];
  phase: ExamPhase;
  attempt: null | {
    startedAt: string;
    submittedAt: string | null;
    partsDone: number;
    resultVisible: boolean;
    /** Faqat `resultVisible` boʻlganda. `null` — yozma ish tekshirilmoqda. */
    score?: number | null;
    grade?: string | null;
    raw?: number | null;
  };
}

/** Bitta savolga saqlangan javob. Har `PUT answer` uni BUTUNLAY almashtiradi. */
export interface ExamAnswer {
  selectedOption: number | null;
  textAnswer: string | null;
  subAnswers: string[];
  files: string[];
}

export interface ExamSessionItem {
  id: string;
  /** Butun imtihon boʻyicha raqam (1..N) — qismlar davom etadi. */
  number: number;
  type: ExamItemType;
  /** Javoblar varaqasida `null`. */
  question: string | null;
  /** Javoblar varaqasida boʻsh — faqat `optionCount` ta harf. */
  options: string[];
  optionCount: number;
  writtenGrading: WrittenGrading | null;
  /** `AUTO`: kalitda nechta band bor (kalitning oʻzi emas). */
  bandCount: number | null;
}

export interface ExamSessionPart {
  id: string;
  order: number;
  title: string;
  durationMin: number;
  maxScore: number;
  itemCount: number;
  startedAt: string | null;
  deadlineAt: string | null;
  submittedAt: string | null;
}

export interface ExamNextPart {
  id: string;
  title: string;
  durationMin: number;
}

export interface ExamSession {
  exam: {
    id: string;
    title: string;
    subject: string | null;
    mode: ExamMode;
    resultMode: ExamResultMode;
    /** Oyna yopiladigan vaqt — qismlar orasidagi ekranda koʻrsatiladi. */
    endsAt: string;
    /** Javoblar varaqasi uchun savollar PDF i (`/uploads/…` yoki https). */
    fileUrl: string | null;
  };
  attempt: { id: string; startedAt: string; serverNow: string };
  parts: ExamSessionPart[];
  /** `between` — bir qism tugadi, keyingisi oʻquvchi bosishini kutmoqda. */
  state: 'active' | 'between';
  current: { id: string; deadlineAt: string } | null;
  next: ExamNextPart | null;
  /** FAQAT hozirgi qismning savollari. Toʻgʻri javob bu yerda YOʻQ. */
  items: ExamSessionItem[];
  answers: Record<string, ExamAnswer>;
}

export type ExamSubmitResult =
  | { submitted: true; finished: false; next: ExamNextPart }
  | { submitted: true; finished: true; resultVisible: boolean };

export interface ExamResultItem {
  id: string;
  number: number;
  partId: string;
  type: ExamItemType;
  writtenGrading: WrittenGrading | null;
  question: string | null;
  options: string[];
  optionCount: number;
  selectedOption: number | null;
  textAnswer: string | null;
  subAnswers: string[];
  files: string[];
  /** Server hisoblagan natija — oʻzimiz qayta hisoblamaymiz. */
  outcome: ExamOutcome;
  /** Yozma + avtomatik: har band toʻgʻrimi. Boshqa turlarda `null`. */
  bandResults: boolean[] | null;
  /** Savol bali (RASCH da `null`). */
  points: number | null;
  awarded: number | null;
  feedback: string | null;
  // Faqat `keyVisible` boʻlganda:
  correctOption?: number | null;
  acceptedAnswers?: string[];
  subKey?: string[][];
  explanation?: string | null;
}

export interface ExamResult {
  exam: {
    id: string;
    title: string;
    subject: string | null;
    mode: ExamMode;
    scoringMethod: 'WEIGHTED' | 'RASCH';
    status: string;
  };
  /** Toʻgʻri javoblar faqat imtihon yopilgach ochiladi. */
  keyVisible: boolean;
  /** `null` — yozma ish hali tekshirilmoqda. */
  score: number | null;
  grade: string | null;
  raw: number | null;
  itemCount: number;
  counts: {
    correct: number;
    partial: number;
    wrong: number;
    unanswered: number;
    pending: number;
    total: number;
  };
  parts: {
    id: string;
    title: string;
    maxScore: number;
    score: number | null;
  }[];
  items: ExamResultItem[];
}

export async function fetchStudentExamsApi(): Promise<StudentExam[]> {
  const res = await axiosClient.get('/students/exams');
  return unwrap<StudentExam[]>(res.data) ?? [];
}

/** Test kodi boʻyicha ("TEST10", katta-kichik harf farqi yoʻq). 404 — topilmadi. */
export async function fetchExamByCodeApi(code: string): Promise<StudentExam> {
  const res = await axiosClient.get(
    `/students/exams/code/${encodeURIComponent(code)}`,
  );
  return unwrap<StudentExam>(res.data);
}

/**
 * Boshlash va davom ettirish — bitta chaqiruv. Keyingi qism FAQAT
 * `startNext` bilan boshlanadi: server uning soatini oʻzi yurgizmaydi.
 */
export async function startExamApi(
  examId: string,
  startNext = false,
): Promise<ExamSession> {
  const res = await axiosClient.post(
    `/students/exams/${examId}/start`,
    startNext ? { startNext: true } : {},
  );
  return unwrap<ExamSession>(res.data);
}

/**
 * Savol turiga qarab bittasi: variant — `selectedOption`, ochiq —
 * `textAnswer`, yozma AUTO — `subAnswers`, yozma MANUAL — `textAnswer` +
 * `files`. Massivlar TOʻLIQ yuboriladi: server javobni almashtiradi.
 */
export interface ExamAnswerInput {
  selectedOption?: number | null;
  textAnswer?: string | null;
  subAnswers?: string[];
  files?: string[];
}

/** Javobni saqlaydi. Server toʻgʻri-notoʻgʻriligini ATAYLAB aytmaydi. */
export async function saveExamAnswerApi(
  examId: string,
  examItemId: string,
  input: ExamAnswerInput,
): Promise<void> {
  await axiosClient.put(`/students/exams/${examId}/answer`, {
    examItemId,
    ...input,
  });
}

/** HOZIRGI qismni topshiradi. Oxirgisi boʻlsa — butun imtihonni. */
export async function submitExamApi(examId: string): Promise<ExamSubmitResult> {
  const res = await axiosClient.post(`/students/exams/${examId}/submit`);
  return unwrap<ExamSubmitResult>(res.data);
}

export async function fetchExamResultApi(examId: string): Promise<ExamResult> {
  const res = await axiosClient.get(`/students/exams/${examId}/result`);
  return unwrap<ExamResult>(res.data);
}
